/**
 * B2B anlık takip ekranı (/b2b-vall) oturum kuralları.
 *
 * - Ekran açıkken oturum düşmez: token bitmeye yaklaşınca arka planda yenilenir.
 * - Ekran kapatılıp (veya bilgisayar uyuyup) LIVE_BOARD_SESSION_TTL_MS'den uzun
 *   süre sonra açılırsa "oturum süreniz doldu" ekranı gösterilir.
 * - Girişten sonra kullanıcı açık olduğu /b2b-vall sayfasına geri döner.
 */
export const LIVE_BOARD_SESSION_TTL_MS = 12 * 60 * 60 * 1000;

const LAST_ACTIVE_KEY = "vall:lastActiveAt";
const REDIRECT_KEY = "vall:postLoginRedirect";
const REDIRECT_MAX_AGE_MS = 60 * 60 * 1000;

const safe = <T,>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export const markLiveBoardActive = () =>
  safe(() => localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now())), undefined);

export const clearLiveBoardActivity = () => safe(() => localStorage.removeItem(LAST_ACTIVE_KEY), undefined);

/** Son başarılı yenilemeden bu yana 12 saatten fazla geçtiyse oturum dolmuş sayılır. */
export const isLiveBoardSessionExpired = (now = Date.now()) => {
  const lastActive = Number(safe(() => localStorage.getItem(LAST_ACTIVE_KEY), null));
  return Number.isFinite(lastActive) && lastActive > 0 && now - lastActive > LIVE_BOARD_SESSION_TTL_MS;
};

export const LIVE_BOARD_BASE_PATH = "/b2b-vall";
/** Eski adres; paylaşılmış linkler bozulmasın diye yeni adrese yönlenir. */
export const LEGACY_LIVE_BOARD_BASE_PATH = "/vall";

export const liveBoardPath = (sessionId?: string) =>
  sessionId ? `${LIVE_BOARD_BASE_PATH}/${sessionId}` : LIVE_BOARD_BASE_PATH;

export const isLiveBoardPath = (path: string) =>
  [LIVE_BOARD_BASE_PATH, LEGACY_LIVE_BOARD_BASE_PATH].some((base) => path === base || path.startsWith(`${base}/`));

/** Anlık takip ekranı açıkken login'e düşülürse girişten sonra buraya dönülür. */
export const rememberLiveBoardRedirect = (path: string) => {
  if (!isLiveBoardPath(path)) return;
  safe(() => sessionStorage.setItem(REDIRECT_KEY, JSON.stringify({ path, at: Date.now() })), undefined);
};

/** Girişten sonra dönülecek anlık takip adresini bir kez verir; yoksa null. */
export const consumeLiveBoardRedirect = (): string | null => {
  const raw = safe(() => sessionStorage.getItem(REDIRECT_KEY), null);
  safe(() => sessionStorage.removeItem(REDIRECT_KEY), undefined);
  if (!raw) return null;
  const parsed = safe(() => JSON.parse(raw), null) as { path?: string; at?: number } | null;
  if (!parsed?.path || !isLiveBoardPath(parsed.path)) return null;
  if (!parsed.at || Date.now() - parsed.at > REDIRECT_MAX_AGE_MS) return null;
  return parsed.path;
};

/** Kayıtlı token verilen süre içinde bitecek mi? (Açık ekranda önceden yenilemek için.) */
export const accessTokenExpiresWithin = (ms: number) => {
  const raw = safe(() => localStorage.getItem("userDetails"), null);
  const details = raw ? (safe(() => JSON.parse(raw), null) as Record<string, any> | null) : null;
  const expireMs = new Date(details?.expireDate || details?.expiresAt || 0).getTime();
  return Number.isFinite(expireMs) && expireMs > 0 && expireMs - Date.now() < ms;
};
